import IdGenerator from 'scripts/domain/contracts/id_generator';

import { InfrastructureError } from 'scripts/application/lib/error';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_silent_handler';
import TeamAssistMessage from 'models/team_assist_message';

// Maximum time we keep the loading indicator up waiting for the agent's
// response to arrive via broadcast. Acts as a safety net in case the broadcast
// is lost or the agent fails server-side without publishing anything.
export const LOADING_SAFETY_TIMEOUT_MS = 60000;

// Per-customer safety-timer ids so a new ask cancels the previous customer's
// pending timer rather than letting it fire later and reset loading on a
// still-in-flight request.
const loadingTimeoutsByCustomerId = new Map();

export default class AskTeamAssist {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, question, displayedMessage }) {
    if (!customerId) {
      return;
    }
    if (!question) {
      return;
    }

    if (!this.context.stores.customers.has({ id: customerId })) {
      return;
    }

    const { teamAssists } = this.context.stores.customers.storesFor(customerId);

    this.context.analytics.track('Team Assist Question Asked', { customerId });

    const messageId = IdGenerator.newId();
    const optimisticMessage = TeamAssistMessage.create({
      content: question,
      createdAt: new Date().toISOString(),
      customerId,
      displayedMessage,
      id: messageId,
      role: TeamAssistMessage.Role.agent_question,
      type: TeamAssistMessage.Type.question,
    });
    teamAssists.addOrReplace(optimisticMessage);
    teamAssists.setLoading();
    startLoadingSafetyTimer(customerId, teamAssists);

    const body = { id: messageId, question };
    if (displayedMessage) {
      body.displayedMessage = displayedMessage;
    }

    // Supernova decides whether to handle this fire-and-forget (response
    // returns only the question; AI messages arrive via MQTT broadcast) or
    // synchronously (response also carries the AI messages inline). Both
    // shapes must be tolerated so this client survives any deploy ordering
    // — including the rollout window where supernova has been upgraded but
    // this bundle has not. We detect the inline shape by the presence of
    // any ai_response message in the response body.
    this.context.gateways.teamAssist
      .add({ customerId }, body)
      .then(response => {
        const aiMessages = (response && response.messages ? response.messages : []).filter(
          m => m && m.role === TeamAssistMessage.Role.ai_response
        );
        if (aiMessages.length === 0) {
          return;
        }
        aiMessages.forEach(m => teamAssists.addOrReplace(TeamAssistMessage.fromJs(m)));
        teamAssists.resetLoading();
        clearLoadingSafetyTimer(customerId);
      })
      .catch(err => {
        // Timeouts and other transport errors do NOT mean the agent failed —
        // the work may still complete and stream back. Only mark the message
        // as failed for definite rejections (validation, permission, etc.)
        // raised before the agent was queued.
        if (!(err instanceof InfrastructureError)) {
          const msg = teamAssists.find(messageId);
          if (msg) {
            teamAssists.addOrReplace(TeamAssistMessage.fromJs({ ...msg.toJs(), error: true }));
          }
          teamAssists.resetLoading();
          clearLoadingSafetyTimer(customerId);
        }
        handleCommonErrors(this.context, err);
      });
  }
}

function startLoadingSafetyTimer(customerId, teamAssists) {
  clearLoadingSafetyTimer(customerId);
  const timeoutId = setTimeout(() => {
    // Re-check ownership: a newer ask may have replaced this timer in the
    // window between scheduling and firing.
    if (loadingTimeoutsByCustomerId.get(customerId) !== timeoutId) {
      return;
    }
    loadingTimeoutsByCustomerId.delete(customerId);
    teamAssists.resetLoading();
  }, LOADING_SAFETY_TIMEOUT_MS);
  loadingTimeoutsByCustomerId.set(customerId, timeoutId);
}

function clearLoadingSafetyTimer(customerId) {
  const timeoutId = loadingTimeoutsByCustomerId.get(customerId);
  if (timeoutId) {
    clearTimeout(timeoutId);
    loadingTimeoutsByCustomerId.delete(customerId);
  }
}
